import { useEffect, useState } from 'react';
import { LogOut, Download, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function Settings() {
    const { user, logout } = useAuth();
    const [stats, setStats] = useState({ exercises: 0, templates: 0, measurements: 0, records: 0, workouts: 0 });

    useEffect(() => {
        (async () => {
            try {
                const [ex, tp, me, rc, hs] = await Promise.all([
                    api.get('/exercises'), api.get('/templates'), api.get('/measurements'), api.get('/records'), api.get('/workouts/history?limit=1000')
                ]);
                setStats({ exercises: ex.data.length, templates: tp.data.length, measurements: me.data.length, records: rc.data.length, workouts: hs.data.length });
            } catch (_) {}
        })();
    }, []);

    const exportData = async () => {
        try {
            const [ex, tp, me, rc, hs] = await Promise.all([
                api.get('/exercises'), api.get('/templates'), api.get('/measurements'), api.get('/records'), api.get('/workouts/history?limit=1000')
            ]);
            const payload = { user, exported_at: new Date().toISOString(), exercises: ex.data, templates: tp.data, measurements: me.data, records: rc.data, workouts: hs.data };
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `fithome_backup_${new Date().toISOString().slice(0,10)}.json`; a.click();
            URL.revokeObjectURL(url);
            toast.success('Backup descargado');
        } catch (e) { toast.error('Error al exportar'); }
    };

    return (
        <div className="px-4 lg:px-8 py-4 lg:py-8 max-w-[900px]">
            <header className="mb-6">
                <div className="text-xs text-muted-foreground uppercase tracking-widest">Tu cuenta</div>
                <h1 className="text-3xl md:text-4xl font-display font-semibold">Ajustes</h1>
            </header>

            <Card className="bg-card border-border p-5 mb-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="h-12 w-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] grid place-items-center font-bold text-lg" data-testid="user-avatar">{(user?.name || '?').charAt(0).toUpperCase()}</div>
                    <div>
                        <div className="font-medium" data-testid="user-name">{user?.name}</div>
                        <div className="text-sm text-muted-foreground" data-testid="user-email">{user?.email}</div>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
                    {[
                        ['Ejercicios', stats.exercises],
                        ['Plantillas', stats.templates],
                        ['Medidas', stats.measurements],
                        ['Records', stats.records],
                        ['Entrenos', stats.workouts],
                    ].map(([l, v]) => (
                        <div key={l} className="p-3 rounded-lg bg-[hsl(var(--surface-2))] border border-border">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
                            <div className="text-lg font-display font-semibold">{v}</div>
                        </div>
                    ))}
                </div>
            </Card>

            <Card className="bg-card border-border p-5 mb-4">
                <div className="flex items-center gap-2 mb-2">
                    <Download className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <div className="font-medium">Exportar tus datos</div>
                </div>
                <p className="text-sm text-muted-foreground mb-3">Descarga un backup de tu cuenta en formato JSON.</p>
                <Button variant="secondary" onClick={exportData} data-testid="export-data-button">Descargar backup</Button>
            </Card>

            <Card className="bg-card border-border p-5 mb-4">
                <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <div className="font-medium">Despliegue en LucusHost</div>
                </div>
                <p className="text-sm text-muted-foreground mb-3">Esta versión corre con FastAPI + MongoDB para previsualización. Descarga el paquete completo con React compilado + PHP + instalador MySQL para subirlo a LucusHost por FTP.</p>
                <Button variant="secondary" asChild data-testid="download-lucushost-package">
                    <a href="/fithome_lucushost.zip" download>
                        <Download className="h-4 w-4 mr-2" /> Descargar paquete LucusHost (.zip)
                    </a>
                </Button>
                <Badge variant="secondary" className="mt-3 ml-2">v1.0.0</Badge>
            </Card>

            <Card className="bg-card border-border p-5">
                <div className="font-medium mb-2">Sesión</div>
                <Button variant="destructive" onClick={logout} data-testid="logout-settings-button"><LogOut className="h-4 w-4 mr-2" /> Cerrar sesión</Button>
            </Card>
        </div>
    );
}
